import React, { useEffect, useState } from "react";
import { Button, Pagination, PlusIcon } from "@pd/pd-ui-kit";
import { Flex } from "antd";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import DeleteSectionGallery from "~/pages/Editor/EditorPhotoGallery/modals/DeleteSectionGallery";
import PhotoGalleryModal from "~/pages/Editor/EditorPhotoGallery/modals/PhotoGalleryModal";
import Wrapper from "~/shared/ui/Wrapper";
import {
  useURLParamsGetter,
  useURLParamsSetter,
} from "~/hooks/useURLParamsSetter";
import { PHOTO_GALLERY_OPTIONS } from "~/pages/Editor/EditorPhotoGallery/utils/options";
import { useGetPhotoGallerySectionListLazyQuery } from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";
import { usePaginationParams } from "~/shared/hooks/usePaginationParams";
import { TableV2 } from "~/shared/ui/TableV2";

import { getEditorPhotoGalleryColumns } from "./model/tableAdapter";

import s from "./s.less";

const EditorPhotoGallery = () => {
  const { t } = useTranslation();
  const { page, count, currentId } = useURLParamsGetter<{
    page: number;
    count: number;
    currentId: string;
  }>();
  const { hasPermission, loading: loadingPermissions } = usePermissions();
  const setParamsReplace = useURLParamsSetter(true);
  const history = useHistory();
  const [sections, setSections] = useState([]);
  const [total, setTotal] = useState(0);
  const goBack = () => history.replace("/editor/");

  const openAddModal = () => {
    setParamsReplace({ openModal: "Create" });
  };
  const [getPhotoGallerySection, getPhotoGallerySectionState] =
    useGetPhotoGallerySectionListLazyQuery({
      fetchPolicy: "no-cache",
    });
  const getPhotoGallerySectionHandler = () => {
    getPhotoGallerySection({
      variables: { page: Number(page), count: Number(count) },
    });
  };

  useEffect(() => {
    getPhotoGallerySectionHandler();
  }, []);

  useEffect(() => {
    if (getPhotoGallerySectionState.loading)
      getPhotoGallerySectionState.data = undefined;

    if (getPhotoGallerySectionState.data) {
      const requestData = get(
        getPhotoGallerySectionState.data,
        "PhotoGallerySectionGetter.getPhotoGallerySectionList",
      );

      if (requestData) {
        setSections(requestData.items);
        setTotal(requestData.total);
      }
    }
  }, [getPhotoGallerySectionState.loading]);

  const currentItem = sections.find((el) => el.id === currentId);

  const { handleCount, handlePage } = usePaginationParams("urlParams");

  const handleRowEdit = (record: { id?: string | number }) => {
    setParamsReplace({ openModal: "Edit", currentId: record.id });
  };

  const handleRowDelete = (record: { id?: string | number }) => {
    setParamsReplace({ openModal: "Sure", currentId: record.id });
  };

  return (
    <Wrapper.Content
      title={t("photoGallery.editor_title")}
      goBackHandler={goBack}
      txtBtn={t("ui.back")}
      controlComponent={
        <Flex gap={10}>
          {hasPermission("photoGallery.section.create") && (
            <Button
              onClick={openAddModal}
              size={"middle"}
              icon={<PlusIcon size={16} />}
            >
              {t("photoGallery.catalogSection")}
            </Button>
          )}
        </Flex>
      }
    >
      <TableV2
        wrapperClassName={s.tableWrapper}
        columns={getEditorPhotoGalleryColumns(t, PHOTO_GALLERY_OPTIONS)}
        dataSource={sections}
        loading={getPhotoGallerySectionState.loading}
        onRowEdit={
          hasPermission("photoGallery.section.update")
            ? handleRowEdit
            : undefined
        }
        onRowDelete={
          hasPermission("photoGallery.section.delete")
            ? handleRowDelete
            : undefined
        }
      />
      <Pagination
        count={Number(count) || 25}
        page={(Number(page) || 0) + 1}
        totalCount={total}
        onChangeCount={handleCount}
        onChangePage={handlePage}
      />

      <PhotoGalleryModal
        reFetch={getPhotoGallerySectionHandler}
        currentItem={currentItem}
      />
      <DeleteSectionGallery reFetch={getPhotoGallerySectionHandler} />
    </Wrapper.Content>
  );
};

export default EditorPhotoGallery;
